import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';
import Store from '../../utilities/store';
import { StateManager } from '../../utilities/stateManager';
import constants from '../../utilities/constants';
import { isRulesAdmin } from '../../utilities/leadDelete';

// Lead routing for one car. The assignLeads cron reads allowedReps and
// excludedReps (sales rep user ids). One mode is active at a time, so only
// one list is ever saved. firestore.rules lets only admins change them, so
// other users see nothing here.
const MODES = {
  everyone: { label: 'Everyone' },
  only: { label: 'Only these reps', key: 'allowedReps', color: 'success', icon: <CheckIcon /> },
  except: { label: 'Everyone except', key: 'excludedReps', color: 'error', icon: <BlockIcon /> },
};

const plural = n => `${n} rep${n === 1 ? '' : 's'}`;

const summaryOf = (mode, count) => {
  if (mode === 'everyone') return 'Every sales rep is in the rotation for this car.';
  if (count === 0) return 'Pick reps below. Until then, every rep gets leads.';
  return mode === 'only'
    ? `Only ${plural(count)} get leads for this car.`
    : `${plural(count)} never get leads for this car.`;
};

export default function CarLeadRouting({ car }) {
  const allowed = car.allowedReps || [];
  const excluded = car.excludedReps || [];
  const [mode, setMode] = useState(allowed.length ? 'only' : excluded.length ? 'except' : 'everyone');
  // Picks per mode stay in memory, so switching modes and back keeps them.
  const [picks, setPicks] = useState({ only: allowed, except: excluded });

  if (!isRulesAdmin(StateManager)) return null;

  const reps = constants.sales || [];
  const selected = picks[mode] || [];

  const save = async (nextMode, nextPicks) => {
    const lists = {
      allowedReps: nextMode === 'only' ? nextPicks.only : [],
      excludedReps: nextMode === 'except' ? nextPicks.except : [],
    };
    const before = { mode, picks };
    setMode(nextMode);
    setPicks(nextPicks);
    try {
      await Store.setCarLeadReps(car.stock, lists);
      if (car.updater) car.updater({ ...car, ...lists });
    } catch (e) {
      setMode(before.mode);
      setPicks(before.picks);
      StateManager.setAlertAndOpen(`Could not save lead routing: ${e.message}`, 'error');
    }
  };

  const changeMode = (e, next) => {
    if (next && next !== mode) save(next, picks);
  };

  const toggleRep = id => {
    const list = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id];
    save(mode, { ...picks, [mode]: list });
  };

  const { color, icon } = MODES[mode];

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
        <Box>
          <Typography variant="subtitle2">Who gets leads for this car</Typography>
          <Typography variant="body2" color="text.secondary">{summaryOf(mode, selected.length)}</Typography>
        </Box>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={mode}
          onChange={changeMode}
          aria-label="Lead routing mode"
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5 } }}
        >
          {Object.entries(MODES).map(([value, m]) => (
            <ToggleButton key={value} value={value}>{m.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {mode !== 'everyone' && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
          {reps.map(rep => {
            const on = selected.includes(rep.id);
            return (
              <Chip
                key={rep.id}
                label={rep.name}
                clickable
                onClick={() => toggleRep(rep.id)}
                color={on ? color : 'default'}
                variant={on ? 'filled' : 'outlined'}
                icon={on ? icon : undefined}
                aria-pressed={on}
              />
            );
          })}
        </Box>
      )}
    </Paper>
  );
}
